import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Container, Box, Typography, CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import LogoGrupoLae from '../../img/image_2024_04_30T07_32_45_647Z.png';
import LogoTSP from '../../img/LOGO_TSP_2.png';
import BackgroundImage from '../../img/Fondo_GRUPO_LAE.jpg';
import { fetchValidarToken, fetchRestablecerContraseña } from '../../../peticiones_API/PeticionesAuth';

const isTSP = window.location.hostname.startsWith('tsp.');
const Logo = isTSP ? LogoTSP : LogoGrupoLae;

const validarContrasena = (contrasena) => {
    const longitudMinima = contrasena.length >= 8;
    const tieneMayuscula = /[A-Z]/.test(contrasena);
    const tieneMinuscula = /[a-z]/.test(contrasena);
    const tieneNumero = /[0-9]/.test(contrasena);
    const tieneCaracterEspecial = /[!@#$%^&*(),.?":{}|<>]/.test(contrasena);

    return {
        longitudMinima,
        tieneMayuscula,
        tieneMinuscula,
        tieneNumero,
        tieneCaracterEspecial
    };
};

const FormNuevaPassRecuperacion = ({ setAutenticado }) => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errorValidacionContrasena, setErrorValidacionContrasena] = useState({
        longitudMinima: false,
        tieneMayuscula: false,
        tieneMinuscula: false,
        tieneNumero: false,
        tieneCaracterEspecial: false
    });
    const navigate = useNavigate();

    useEffect(() => {
        // Si el usuario llega con una sesión previa, la limpiamos: el enlace de
        // recuperación siempre debe permitir establecer una nueva contraseña.
        if (localStorage.getItem("token")) {
            localStorage.removeItem("token");
            localStorage.removeItem("requires2FASetup");
            if (typeof setAutenticado === "function") {
                setAutenticado(false);
            }
        }

        if (token) {
            validarToken();
        } else {
            setLoading(false);
            setError(true);
        }
    }, []);

    useEffect(() => {
        if (password) {
            const validacion = validarContrasena(password);
            setErrorValidacionContrasena(validacion);
        }
    }, [password]);

    const validarToken = async () => {
        try {
            const response = await fetchValidarToken({ token: token });
            if (response.status !== 200) {
                setError(true);
            } else {
                setError(false);
            }
        } catch (error) {
            console.log(error);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validar que la contraseña cumpla con todos los requisitos
        const validacion = validarContrasena(password);
        setErrorValidacionContrasena(validacion);

        if (!Object.values(validacion).every(Boolean)) {
            toast.error("La contraseña no cumple con los requisitos");
            return;
        }

        // Validar que las contraseñas coincidan
        if (!password || !confirmPassword || password !== confirmPassword) {
            toast.error("Las contraseñas no coinciden");
            return;
        }

        setLoadingBtn(true);
        try {
            const response = await fetchRestablecerContraseña({
                token: token,
                password: password,
                confirmPassword: confirmPassword
            });

            if (response.status === 200) {
                toast.success("Contraseña restablecida correctamente");

                setTimeout(() => {
                    navigate("/login");
                }, 3000);

            } else {
                toast.error("No se pudo restablecer la contraseña");
            }
        } catch (error) {
            console.error("Error al restablecer la contraseña:", error);
            toast.error("Error al restablecer la contraseña");
        } finally {
            setLoadingBtn(false);
        }
    }

    if (loading) {
        return (<p>Cargando...</p>);
    }
    if (error) {
        return (<p>Error: El enlace ha expirado o no es válido</p>);
    }

    return ( 

        <Box
            sx={{
                minHeight: '100vh',
                backgroundImage: `url(${BackgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2
            }}
        >

        <Container
            component="main"
            maxWidth="xs"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh'
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 2,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: 'white',
                    width: '100%',
                    maxWidth: 360
                }}
            >
                <img src={Logo} alt="Logo" style={{ width: '100px', marginBottom: '20px' }} />
                <Typography component="h1" variant="h5">
                    Restablecer la contraseña
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Nueva Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    
                    {password && (
                        <Box sx={{ mt: 1, mb: 2, fontSize: '0.75rem' }}>
                            <Typography variant="subtitle2">Requisitos de contraseña:</Typography>
                            <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                                <li style={{ color: errorValidacionContrasena.longitudMinima ? 'green' : 'red', marginBottom: '2px' }}>
                                    {errorValidacionContrasena.longitudMinima ? '✓' : '✗'} Mínimo 8 caracteres
                                </li>
                                <li style={{ color: errorValidacionContrasena.tieneMayuscula ? 'green' : 'red', marginBottom: '2px' }}>
                                    {errorValidacionContrasena.tieneMayuscula ? '✓' : '✗'} Al menos una mayúscula
                                </li>
                                <li style={{ color: errorValidacionContrasena.tieneMinuscula ? 'green' : 'red', marginBottom: '2px' }}>
                                    {errorValidacionContrasena.tieneMinuscula ? '✓' : '✗'} Al menos una minúscula
                                </li>
                                <li style={{ color: errorValidacionContrasena.tieneNumero ? 'green' : 'red', marginBottom: '2px' }}>
                                    {errorValidacionContrasena.tieneNumero ? '✓' : '✗'} Al menos un número
                                </li>
                                <li style={{ color: errorValidacionContrasena.tieneCaracterEspecial ? 'green' : 'red', marginBottom: '2px' }}>
                                    {errorValidacionContrasena.tieneCaracterEspecial ? '✓' : '✗'} Al menos un carácter especial
                                </li>
                            </ul>
                        </Box>
                    )}
                    
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirmar Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1 }}
                        disabled={loadingBtn}
                    >
                        {loadingBtn ? (
                            <CircularProgress
                                thickness={5}
                                size={24}
                                style={{ color: '#ffffff', position: 'relative', top: 3, marginBottom: 5 }}
                            />
                        ) : (
                            'Restablecer contraseña'
                        )}
                    </Button>
                </Box>
            </Box>
        </Container>
        </Box>
    );
}

export default FormNuevaPassRecuperacion;